// components/RecommendationActionDialog.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Checkbox,
  Chip,
  CircularProgress,
  Paper,
  IconButton,
  Divider,
  LinearProgress,
  Grid
} from '@material-ui/core';
import {
  SwapHoriz,
  ArrowForward,
  CheckCircle,
  Warning,
  Schedule,
  DirectionsCar,
  Build,
  Close,
  Undo,
  PlayArrow
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import moment from 'moment';
import { getSmartJobSuggestions, getOptimalJobCombination } from '../utils/smartRebalancing';

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    minHeight: 400,
    maxHeight: '70vh'
  },
  stepContent: {
    paddingTop: theme.spacing(2)
  },
  crewCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1),
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: theme.palette.action.hover
    },
    '&.selected': {
      borderColor: theme.palette.primary.main,
      borderWidth: 2
    }
  },
  fromCrew: {
    backgroundColor: '#ffebee',
    borderLeft: `4px solid ${theme.palette.error.main}`
  },
  toCrew: {
    backgroundColor: '#e8f5e9',
    borderLeft: `4px solid ${theme.palette.success.main}`
  },
  jobItem: {
    marginBottom: theme.spacing(0.5),
    '&.selected': {
      backgroundColor: theme.palette.action.selected
    }
  },
  summaryBox: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(2)
  },
  impactChip: {
    margin: theme.spacing(0.5)
  }
}));

const RecommendationActionDialog = ({
  open,
  onClose,
  recommendation,
  crewMetrics,
  weeklyRoutes,
  onExecuteAction,
  sites,
  crews
}) => {
  const classes = useStyles();
  const [activeStep, setActiveStep] = useState(0);
  const [selectedFromCrew, setSelectedFromCrew] = useState(null);
  const [selectedToCrew, setSelectedToCrew] = useState(null);
  const [selectedJobs, setSelectedJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionSummary, setActionSummary] = useState(null);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setActiveStep(0);
      setSelectedFromCrew(null);
      setSelectedToCrew(null);
      setSelectedJobs([]);
      setActionSummary(null);
    }
  }, [open]);

  const getStepsForRecommendation = () => {
    switch (recommendation?.type) {
      case 'workload_rebalance':
        return ['Select Overworked Crew', 'Select Available Crew', 'Choose Jobs to Transfer', 'Review & Execute'];
      case 'overtime_reduction':
        return ['Review Overtime Crews', 'Select Jobs to Redistribute', 'Assign to Available Crews', 'Review & Execute'];
      case 'territory_redesign':
        return ['Identify Problem Areas', 'Select Jobs to Reassign', 'Choose New Assignments', 'Review & Execute'];
      case 'route_optimization':
        return ['Select Crews to Optimize', 'Review Current Routes', 'Apply Optimization', 'Review & Execute'];
      default:
        return ['Review', 'Execute'];
    }
  };

  const steps = getStepsForRecommendation();

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleJobToggle = (job) => {
    setSelectedJobs(prev => {
      const isSelected = prev.some(j => j._id === job._id);
      if (isSelected) {
        return prev.filter(j => j._id !== job._id);
      } else {
        return [...prev, job];
      }
    });
  };

  const calculateImpact = () => {
    if (!selectedJobs.length) return null;

    const totalHours = selectedJobs.reduce((sum, job) => sum + (job.estimatedHours || 0), 0);
    const totalMiles = selectedJobs.reduce((sum, job) => sum + (job.estimatedDriveTime || 0) * 0.5, 0); // Rough estimate

    return {
      hoursTransferred: totalHours,
      milesReduced: totalMiles,
      fromCrewNewTotal: (crewMetrics[selectedFromCrew]?.workload.weeklyHours || 0) - totalHours,
      toCrewNewTotal: (crewMetrics[selectedToCrew]?.workload.weeklyHours || 0) + totalHours,
      efficiencyImprovement: Math.round((totalMiles / 50) * 10) // Rough estimate
    };
  };

  const executeAction = async () => {
    setLoading(true);
    const impact = calculateImpact();
    
    try {
      // Create action object based on recommendation type
      const action = {
        type: recommendation.type,
        fromCrew: selectedFromCrew,
        toCrew: selectedToCrew,
        jobs: selectedJobs,
        impact,
        timestamp: new Date()
      };

      // Call parent's execute function
      if (onExecuteAction) {
        await onExecuteAction(action);
      }

      setActionSummary({
        success: true,
        message: `Successfully transferred ${selectedJobs.length} jobs`,
        impact
      });

      // Move to final step
      setActiveStep(steps.length);
    } catch (error) {
      setActionSummary({
        success: false,
        message: 'Failed to execute action: ' + error.message
      });
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0: // Select source crew
        if (recommendation?.type === 'workload_rebalance') {
          const overworkedCrews = Object.values(crewMetrics).filter(
            crew => crew.workload.overtime > 4
          );
          
          return (
            <Box className={classes.stepContent}>
              <Typography variant="body2" paragraph>
                Select the overworked crew to redistribute jobs from:
              </Typography>
              <List>
                {overworkedCrews.map(crew => (
                  <Paper
                    key={crew.crewId}
                    className={`${classes.crewCard} ${classes.fromCrew} ${
                      selectedFromCrew === crew.crewId ? 'selected' : ''
                    }`}
                    onClick={() => setSelectedFromCrew(crew.crewId)}
                    variant="outlined"
                  >
                    <Grid container alignItems="center" spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                          {crew.crewName}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {crew.workload.weeklyHours.toFixed(1)}h total
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Warning color="error" />
                          <Typography variant="body2" color="error">
                            {crew.workload.overtime.toFixed(1)}h overtime
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min((crew.workload.weeklyHours / 40) * 100, 100)}
                          style={{ marginTop: 4 }}
                          color="secondary"
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
              </List>
            </Box>
          );
        }
        break;

      case 1: // Select target crew
        if (recommendation?.type === 'workload_rebalance') {
          const availableCrews = Object.values(crewMetrics).filter(
            crew => crew.workload.utilization < 80 && crew.crewId !== selectedFromCrew
          );
          
          return (
            <Box className={classes.stepContent}>
              <Typography variant="body2" paragraph>
                Select the crew with available capacity:
              </Typography>
              <List>
                {availableCrews.map(crew => (
                  <Paper
                    key={crew.crewId}
                    className={`${classes.crewCard} ${classes.toCrew} ${
                      selectedToCrew === crew.crewId ? 'selected' : ''
                    }`}
                    onClick={() => setSelectedToCrew(crew.crewId)}
                    variant="outlined"
                  >
                    <Grid container alignItems="center" spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                          {crew.crewName}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {crew.workload.weeklyHours.toFixed(1)}h / 40h
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <CheckCircle style={{ color: '#4caf50' }} />
                          <Typography variant="body2" style={{ color: '#4caf50' }}>
                            {(40 - crew.workload.weeklyHours).toFixed(1)}h available
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={(crew.workload.weeklyHours / 40) * 100}
                          style={{ marginTop: 4 }}
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
              </List>
            </Box>
          );
        }
        break;

      case 2: // Select jobs to transfer
        if (selectedFromCrew && selectedToCrew && weeklyRoutes) {
          // Get jobs for the selected crew
          const crewJobs = [];
          weeklyRoutes.forEach(dayRoute => {
            const route = dayRoute.routes?.find(r => r.crewId === selectedFromCrew);
            if (route?.stops) {
              route.stops.forEach(stop => {
                const site = sites?.find(s => s._id === stop.siteId);
                if (site) {
                  crewJobs.push({
                    ...site,
                    day: dayRoute.day,
                    estimatedHours: stop.estimatedServiceDuration / 60 || site.estimatedHours || 0,
                    distanceFromBase: stop.estimatedDriveDuration || 0
                  });
                }
              });
            }
          });

          // Get smart suggestions
          const fromMetrics = crewMetrics[selectedFromCrew];
          const toMetrics = crewMetrics[selectedToCrew];
          const smartJobs = getSmartJobSuggestions(crewJobs, fromMetrics, toMetrics);
          
          // Calculate optimal combination
          const targetHours = fromMetrics.workload.overtime || 4;
          const optimal = getOptimalJobCombination(smartJobs, targetHours);

          return (
            <Box className={classes.stepContent}>
              <Typography variant="body2" paragraph>
                Select jobs to transfer (AI-ranked by impact):
              </Typography>
              
              {optimal.jobs.length > 0 && (
                <Paper style={{ padding: 12, marginBottom: 16, backgroundColor: '#e3f2fd' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    🤖 Smart Suggestion
                  </Typography>
                  <Typography variant="body2" paragraph>
                    Transfer these {optimal.jobs.length} jobs for optimal impact 
                    ({optimal.totalHours.toFixed(1)}h total)
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={() => setSelectedJobs(optimal.jobs)}
                  >
                    Apply Suggestion
                  </Button>
                </Paper>
              )}
              
              <List style={{ maxHeight: 300, overflow: 'auto' }}>
                {smartJobs.map((job, index) => (
                  <ListItem
                    key={`${job._id}-${index}`}
                    className={`${classes.jobItem} ${
                      selectedJobs.some(j => j._id === job._id) ? 'selected' : ''
                    }`}
                    dense
                    button
                    onClick={() => handleJobToggle(job)}
                  >
                    <ListItemIcon>
                      <Checkbox
                        checked={selectedJobs.some(j => j._id === job._id)}
                        tabIndex={-1}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body2">{job.name}</Typography>
                          {job.transferScore > 50 && (
                            <Chip
                              size="small"
                              label={`Score: ${Math.round(job.transferScore)}`}
                              style={{ 
                                backgroundColor: '#4caf50', 
                                color: 'white',
                                height: 20
                              }}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <React.Fragment>
                          <Typography variant="caption" display="block">
                            {job.day} - {job.estimatedHours.toFixed(1)}h - {job.address}
                          </Typography>
                          <Typography variant="caption" color="primary">
                            {job.transferReasons?.join(' • ')}
                          </Typography>
                        </React.Fragment>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Chip
                        size="small"
                        label={`${job.estimatedHours.toFixed(1)}h`}
                        color={job.estimatedHours > 2 ? 'secondary' : 'default'}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
              {selectedJobs.length > 0 && (
                <Box mt={2}>
                  <Typography variant="body2" color="primary">
                    Selected: {selectedJobs.length} jobs, {
                      selectedJobs.reduce((sum, j) => sum + j.estimatedHours, 0).toFixed(1)
                    }h total
                  </Typography>
                </Box>
              )}
            </Box>
          );
        }
        break;

      case 3: // Review and execute
        const impact = calculateImpact();
        const fromCrewData = crewMetrics[selectedFromCrew];
        const toCrewData = crewMetrics[selectedToCrew];

        return (
          <Box className={classes.stepContent}>
            <Typography variant="h6" gutterBottom>
              Review Action Summary
            </Typography>
            
            <Paper className={classes.summaryBox}>
              <Typography variant="subtitle2" gutterBottom>
                Transfer Details:
              </Typography>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Chip 
                  label={fromCrewData?.crewName} 
                  style={{ backgroundColor: '#ffcdd2' }}
                />
                <ArrowForward />
                <Chip 
                  label={toCrewData?.crewName}
                  style={{ backgroundColor: '#c8e6c9' }}
                />
              </Box>
              
              <Typography variant="body2" paragraph>
                • {selectedJobs.length} jobs to be transferred
              </Typography>
              <Typography variant="body2" paragraph>
                • {impact?.hoursTransferred.toFixed(1)} hours of work
              </Typography>
              
              <Divider style={{ margin: '16px 0' }} />
              
              <Typography variant="subtitle2" gutterBottom>
                Expected Impact:
              </Typography>
              <Box display="flex" flexWrap="wrap">
                <Chip
                  icon={<Schedule />}
                  label={`${fromCrewData?.crewName}: ${impact?.fromCrewNewTotal.toFixed(1)}h (was ${fromCrewData?.workload.weeklyHours.toFixed(1)}h)`}
                  className={classes.impactChip}
                  color="primary"
                />
                <Chip
                  icon={<Schedule />}
                  label={`${toCrewData?.crewName}: ${impact?.toCrewNewTotal.toFixed(1)}h (was ${toCrewData?.workload.weeklyHours.toFixed(1)}h)`}
                  className={classes.impactChip}
                  color="primary"
                />
                {impact?.efficiencyImprovement > 0 && (
                  <Chip
                    icon={<TrendingUp />}
                    label={`+${impact.efficiencyImprovement}% efficiency`}
                    className={classes.impactChip}
                    style={{ backgroundColor: '#4caf50', color: 'white' }}
                  />
                )}
              </Box>
            </Paper>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {recommendation?.title || 'Execute Action'}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent className={classes.dialogContent}>
        {actionSummary ? (
          <Box textAlign="center" py={4}>
            {actionSummary.success ? (
              <>
                <CheckCircle style={{ fontSize: 64, color: '#4caf50' }} />
                <Typography variant="h6" style={{ marginTop: 16 }}>
                  Action Completed Successfully!
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                  {actionSummary.message}
                </Typography>
              </>
            ) : (
              <>
                <Warning style={{ fontSize: 64, color: '#f44336' }} />
                <Typography variant="h6" style={{ marginTop: 16 }}>
                  Action Failed
                </Typography>
                <Typography variant="body2" color="error" style={{ marginTop: 8 }}>
                  {actionSummary.message}
                </Typography>
              </>
            )}
          </Box>
        ) : (
          <Stepper activeStep={activeStep} orientation="vertical">
            {steps.map((label, index) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
                <StepContent>
                  {renderStepContent(index)}
                  <Box mt={2}>
                    <Button
                      disabled={activeStep === 0 || loading}
                      onClick={handleBack}
                    >
                      Back
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={activeStep === steps.length - 1 ? executeAction : handleNext}
                      disabled={
                        loading ||
                        (activeStep === 0 && !selectedFromCrew) ||
                        (activeStep === 1 && !selectedToCrew) ||
                        (activeStep === 2 && selectedJobs.length === 0)
                      }
                      style={{ marginLeft: 8 }}
                    >
                      {loading ? (
                        <CircularProgress size={20} />
                      ) : activeStep === steps.length - 1 ? (
                        <>
                          <PlayArrow style={{ marginRight: 4 }} />
                          Execute
                        </>
                      ) : (
                        'Next'
                      )}
                    </Button>
                  </Box>
                </StepContent>
              </Step>
            ))}
          </Stepper>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>
          {actionSummary ? 'Close' : 'Cancel'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecommendationActionDialog;